import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument } from '../firebase';
import RoleSignupList from './RoleSignupList';

export default function DinnerDishes({ users = [] }) {
  const [dinners, setDinners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signingUpId, setSigningUpId] = useState(null);
  const [signingUpRole, setSigningUpRole] = useState(""); // chef or washer
  const [userNameInput, setUserNameInput] = useState("");
  const [editingMenuId, setEditingMenuId] = useState(null);

  // Form states for menu edit
  const [editTheme, setEditTheme] = useState("");
  const [editMenu, setEditMenu] = useState("");

  useEffect(() => {
    // Subscribe to Dinners database
    const unsubscribe = subscribeToCollection("dinners", (data) => {
      // Sort day-by-day chronologically
      const order = ["Saturday 6/13", "Sunday 6/14", "Monday 6/15", "Tuesday 6/16", "Wednesday 6/17", "Thursday 6/18", "Friday 6/19"];
      const sorted = [...data].sort((a, b) => order.indexOf(a.day) - order.indexOf(b.day));

      setDinners(sorted);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Deep-link handler: scroll to and highlight a specific dinner via sessionStorage
  useEffect(() => {
    if (dinners.length === 0) return;
    const targetId = sessionStorage.getItem('prine_focus_dinner');
    if (!targetId) return;
    sessionStorage.removeItem('prine_focus_dinner');
    setTimeout(() => {
      const el = document.getElementById(`dinner-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s ease, outline 0.3s ease';
        el.style.outline = '2px solid var(--color-accent)';
        el.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.2)';
        setTimeout(() => {
          el.style.outline = '';
          el.style.boxShadow = '';
        }, 2200);
      }
    }, 80);
  }, [dinners]);

  const getNamesList = (str) => {
    if (!str) return [];
    return str.split(',').map(n => n.trim()).filter(Boolean);
  };

  const handleStartSignup = (dinnerId, role) => {
    setSigningUpId(dinnerId);
    setSigningUpRole(role);
    const savedName = localStorage.getItem("prine_user_name") || (users[0]?.id || "");
    setUserNameInput(savedName);
  };

  const handleConfirmSignup = async (dinner) => {
    if (!userNameInput.trim()) return alert("Please enter your name.");

    const currentRoleValue = dinner[signingUpRole] || "";
    const names = getNamesList(currentRoleValue);
    const newName = userNameInput.trim();
    if (!names.includes(newName)) {
      names.push(newName);
    }

    const updated = {
      ...dinner,
      [signingUpRole]: names.join(", ")
    };

    // Removed localStorage mutation
    await saveDocument("dinners", dinner.id, updated);
    setSigningUpId(null);
  };

  const handleResign = async (dinner, role, nameToRemove) => {
    if (!window.confirm(`Are you sure you want to remove this person from this role?`)) return;
    const currentRoleValue = dinner[role] || "";
    const names = getNamesList(currentRoleValue);
    const updatedNames = names.filter(n => n !== nameToRemove);

    const updated = {
      ...dinner,
      [role]: updatedNames.join(", ")
    };
    await saveDocument("dinners", dinner.id, updated);
  };

  const handleEditMenu = (dinner) => {
    setEditingMenuId(dinner.id);
    setEditTheme(dinner.theme);
    setEditMenu(dinner.menu);
  };

  const handleSaveMenu = async (dinner) => {
    const updated = {
      ...dinner,
      theme: editTheme.trim(),
      menu: editMenu.trim()
    };
    await saveDocument("dinners", dinner.id, updated);
    setEditingMenuId(null);
  };

  return (
    <div className="tab-panel">
      {/* Normalize all styles to Montserrat body font */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .dinner-card, .dinner-day-label, .dinner-theme-name, .dinner-menu-details, .role-badge, .role-assignee, .btn-role-signup, .input-field, select, button {
          font-family: var(--font-body) !important;
        }
      `}} />

      <div className="view-title-container">
        <div className="view-title">
          <h2>🍳 Dinner & Clean-Up Signup</h2>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-chip">
            <span className="spinner"></span>
            <span>Syncing Dinner Board...</span>
          </div>
        </div>
      ) : dinners.length === 0 ? (
        <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', width: '100%' }}>
          🍽️ Dinner schedule is empty!
        </div>
      ) : (
        <div className="dinner-timeline">
          {dinners.map(dinner => {
            const isSigningUp = signingUpId === dinner.id;
            const isEditingMenu = editingMenuId === dinner.id;

            return (
              <div key={dinner.id} id={`dinner-${dinner.id}`} className="dinner-card">
                <span className="dinner-day-label">{dinner.day}</span>

                {isEditingMenu ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    <input
                      type="text"
                      className="input-field"
                      value={editTheme}
                      onChange={e => setEditTheme(e.target.value)}
                      placeholder="Theme/Dinner Title"
                    />
                    <textarea
                      className="input-field"
                      value={editMenu}
                      onChange={e => setEditMenu(e.target.value)}
                      placeholder="Menu descriptions"
                      rows="3"
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn-primary" onClick={() => handleSaveMenu(dinner)}>Save</button>
                      <button className="btn-secondary" onClick={() => setEditingMenuId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="dinner-menu-box" onClick={() => handleEditMenu(dinner)} title="Click to edit menu">
                    <div className="dinner-theme-name">{dinner.theme || "TBD (Click to Edit)"}</div>
                    <div className="dinner-menu-details">{dinner.menu || "Click to add menu details..."}</div>
                    <span style={{ fontSize: '0.65rem', color: 'var(--color-primary)', display: 'block', marginTop: '8px', fontWeight: '600' }}>
                      Click to edit menu details
                    </span>
                  </div>
                )}

                <div className="dinner-roles-section" style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                  {/* Chef Role */}
                  <RoleSignupList
                    label="👨‍🍳 Chefs"
                    names={getNamesList(dinner.chef)}
                    isSigningUp={isSigningUp && signingUpRole === "chef"}
                    userNameInput={userNameInput}
                    onUserNameChange={setUserNameInput}
                    onStartSignup={() => handleStartSignup(dinner.id, "chef")}
                    onConfirmSignup={() => handleConfirmSignup(dinner)}
                    onCancelSignup={() => setSigningUpId(null)}
                    onLeave={(name) => handleResign(dinner, "chef", name)}
                    users={users}
                    buttonLabel="+ Sign Up"
                    borderColor="rgba(14, 165, 233, 0.18)"
                    accentColor="var(--color-primary)"
                  />

                  {/* Washer/Clean-up Role */}
                  <RoleSignupList
                    label="🧽 Clean-up"
                    names={getNamesList(dinner.washer)}
                    isSigningUp={isSigningUp && signingUpRole === "washer"}
                    userNameInput={userNameInput}
                    onUserNameChange={setUserNameInput}
                    onStartSignup={() => handleStartSignup(dinner.id, "washer")}
                    onConfirmSignup={() => handleConfirmSignup(dinner)}
                    onCancelSignup={() => setSigningUpId(null)}
                    onLeave={(name) => handleResign(dinner, "washer", name)}
                    users={users}
                    buttonLabel="+ Sign Up"
                    borderColor="rgba(168, 85, 247, 0.18)"
                    accentColor="var(--color-secondary)"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
